/** Inert wrapper tags that embed providers put around their `<iframe>`. */
const INERT_TAGS = new Set(["DIV", "SPAN", "P", "SECTION", "FIGURE", "IFRAME"]);

/**
 * If `code` is nothing more than a single `<iframe src="http(s)://…">` (optionally
 * wrapped in inert layout tags, like YouTube/Vimeo/Loom snippets), returns that
 * src so the caller can render a plain cross-origin iframe. The third party then
 * runs on its real origin, with a Referer, exactly like the "link" embed mode.
 *
 * Anything else (scripts, inline handlers, `srcdoc`, text, a same-origin src)
 * returns `null` and must be rendered in the origin-isolated sandbox instead.
 * Parsing uses DOMParser, which never executes scripts or loads resources.
 */
export const extractDirectIframeSrc = (code: string): string | null => {
  if (!code || typeof DOMParser === "undefined") return null;
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(code, "text/html");
  } catch {
    return null;
  }
  // Anything the parser hoisted into <head> (<script>, <style>, <meta>…) disqualifies.
  if (doc.head.childNodes.length > 0) return null;

  let iframe: HTMLIFrameElement | null = null;
  for (const el of Array.from(doc.body.querySelectorAll("*"))) {
    if (!INERT_TAGS.has(el.tagName)) return null;
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on") || name === "srcdoc") return null;
    }
    if (el.tagName === "IFRAME") {
      if (iframe) return null; // more than one frame → not a simple snippet
      iframe = el as HTMLIFrameElement;
    }
  }
  if (!iframe) return null;
  if ((doc.body.textContent ?? "").trim() !== "") return null;

  const src = iframe.getAttribute("src")?.trim() ?? "";
  let url: URL;
  try {
    url = new URL(src);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return null;
  // A frame on our own origin would be same-origin with the host page.
  if (typeof window !== "undefined" && url.origin === window.location.origin) {
    return null;
  }
  return url.href;
};
